import { useState } from 'react';
import { Alert, Box, Button, FileInput, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  onDownloadTemplate: () => Promise<void>;
  isDownloadingTemplate: boolean;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  onDeployedDownload: () => Promise<void>;
  isDeployedDownloading: boolean;
};

export default function TitlePageSection({
  onDownloadTemplate,
  isDownloadingTemplate,
  onUpload,
  isUploading,
  onDeployedDownload,
  isDeployedDownloading,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const theme = useMantineTheme();

  const handleUpload = async () => {
    if (!file) return;
    try {
      await onUpload(file);
      notifications.show({
        title: 'Uploaded',
        message: 'title_page.docx has been uploaded and deployed.',
        color: 'green',
      });
      setFile(null);
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'Failed to upload the title page.',
        color: 'red',
      });
    }
  };

  const containerStyle = {
    borderRadius: 16,
    border: `1px solid ${theme.colors.gray[2]}`,
    overflow: 'hidden' as const,
  };

  return (
    <Stack gap="md">
      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Template</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Generates a single-page .docx with your current branding styles and three Jinja tags
            (&#123;&#123;CLIENT_NAME&#125;&#125;, &#123;&#123;CURRENT_DATE&#125;&#125;,
            &#123;&#123;ADVISOR_NAME&#125;&#125;) in Heading 2. Open it in Word, add a full-page
            background image, then re-upload below.
          </Text>
          <Button
            onClick={onDownloadTemplate}
            loading={isDownloadingTemplate}
            style={{ alignSelf: 'flex-start' }}
          >
            Download Template
          </Button>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Upload Title Page</Text>
          <Alert color="yellow" variant="light">
            Uploading will overwrite the currently deployed title_page.docx for this tenant.
          </Alert>
          <FileInput
            label="Upload title_page.docx"
            placeholder="Select .docx file"
            accept=".docx"
            value={file}
            onChange={setFile}
          />
          <Group>
            <Button
              onClick={handleUpload}
              loading={isUploading}
              disabled={!file}
              variant="light"
              color="orange"
            >
              Upload Title Page
            </Button>
          </Group>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Deployed Title Page</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Downloads the currently deployed title_page.docx from S3.
          </Text>
          <Button
            onClick={onDeployedDownload}
            loading={isDeployedDownloading}
            variant="light"
            style={{ alignSelf: 'flex-start' }}
          >
            Download Deployed File
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
